import { IconDocumentMulti } from '@lmig/lmds-react-icons';
import { Heading } from '@lmig/lmds-react-typography';
import { Link } from '@lmig/lmds-react-link';
import { useUserData } from 'Foundation/User/UserContext';
import { useEffect, useState } from 'react';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { isOneBrand } from 'Foundation/Configurations/GlobalConfiguration';
import 'Feature/LinkBox/LinkBox.scss';

import { GetProductGuideModal, ProductGuideModal, ProductGuideModalObject } from 'Feature/PageContent/Models/ProductGuideModal';
import globalConfig from 'Data/globalConfig.json';

type Guide = {
  title: string;
  id: string;
  audience: string[];
  filename: string;
  content_type: string;
};

const Guides = (props: { lob: string | undefined }) => {
  const { userData } = useUserData();
  const [lob, setLob] = useState<string>();
  const [archivedGuidesUrl, setArchivedGuidesUrl] = useState<string>();
  const [agentGuides, setAgentGuides] = useState<Guide[]>();
  const [employeeGuides, setEmployeeGuides] = useState<Guide[]>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalHeader, setModalHeader] = useState<string>('');
  const [modalBody, setModalBody] = useState<string>('');
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);

  useEffect(() => {
    switch (props.lob) {
      case 'Pet Insurance':
        setLob('pet');
        break;
      case 'Home':
        setLob('homeowners');
        break;
      case 'Motorhome':
        setLob('rv');
        break;
      case 'Snowmobile,Motorcycle':
        setLob('motorcycle');
        break;
      case 'Golf Cart and LSV':
        setLob('motorcycle');
        break;
      case 'ATV and Off-Road Vehicles,Motorcycle':
        setLob('motorcycle');
        break;
      default:
        setLob(props.lob?.toLocaleLowerCase());
    }
  }, [props.lob]);

  useEffect(() => {
    const getGuides = async () => {
      if (userData?.state && userData.isAgent() && lob) {
        await fetch(`/api/sitecore/documents/getmetadata?state=${userData.state}&lob=${lob}`)
          .then((res) => res.json())
          .then((result) => setAgentGuides(result));
      } else if (userData?.state && !userData.isAgent() && lob) {
        await fetch(`/api/sitecore/documents/getmetadata?state=${userData.state}&lob=${lob}&audience=employee`)
          .then((res) => res.json())
          .then((result) => {
            if (result) {
              setAgentGuides(result.filter((x: Guide) => x.audience.includes('agent')));
              setEmployeeGuides(
                result.filter((x: Guide) => x.audience.includes('employee') && !x.audience.includes('agent')),
              );
            }
          });
      }
    };
    
    if (!agentGuides) getGuides();
    if (lob) setArchivedGuidesUrl(`${globalConfig.Coveo.searchPage}?archived=true&lob=${lob}`);
    else setArchivedGuidesUrl(`${globalConfig.Coveo.searchPage}?archived=true`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.state, lob]);

  useEffect(() => {
    if (isOneBrand() && !modalBody && !modalHeader) {
      GetProductGuideModal().then((result: ProductGuideModalObject) => {
        if (result.ErrorMessage.length === 0) {
          setModalHeader(result.ModalHeader);
          setModalBody(result.ModalBody);
        } else {
          console.error('GetProductGuideModal');
          console.error(result.ErrorMessage);
        }
      });
    }
  }, [modalBody, modalHeader])

  const onModalButtonClicked = () => {
    setIsModalOpen(false);
    selectedGuide && window.open(`/docs/guides/${selectedGuide.filename}`, '_blank')
    setSelectedGuide(null);
  };

  const onLinkClicked = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, guide: Guide) => {
    e.preventDefault(); 
    setSelectedGuide(guide); 
    setIsModalOpen(true); 
  };

  const renderGuideLink = (guide: Guide) => (
    <li key={guide.id} className="guides-list-item">
      {isOneBrand() ? (
        <Link href="#" onClick={(e) => onLinkClicked(e, guide)}>
          {guide.title}
        </Link>
      ) : (
        <Link href={`/docs/guides/${guide.filename}`} target="_blank">
          {guide.title}
        </Link>
      )}
    </li>
  );

  const renderGuidesSection = (title: string, guides?: Guide[]) => (
    <section className="guides-section" aria-label={title}>
      <div className="guides-section-header">
        <IconDocumentMulti size="32" />
        <Heading type="h4-light">{title}</Heading>
      </div>
      <ul className="guides-list">
        {guides ? (
          <>
            {guides.length > 0 ? (
              guides.map((guide: Guide) => renderGuideLink(guide))
            ) : (
              <li className="no-guides-message">No guides are currently available.</li>
            )}
            <li className="guides-list-item">
              <Link href={archivedGuidesUrl}>Archived Guides</Link>
            </li>
          </>
        ) : (
          <CircularProgressSpinner size="32" aria-label="Loading product guides..." className="loading-spinner" />
        )}
      </ul>
    </section>
  );

  return (
    <div className="guides">
      {userData && renderGuidesSection('Guides', agentGuides)}

      {userData && !userData.isAgent() && (
        renderGuidesSection('Employee Guides', employeeGuides)
      )}

      {isOneBrand() && selectedGuide && 
        <ProductGuideModal
          isOpen={isModalOpen}
          onClose={() => {setSelectedGuide(null); setIsModalOpen(false)}}
          onConfirm={onModalButtonClicked}
          modalHeader={modalHeader}
          modalBody={modalBody}
      />}
    </div>
  );
};

export default Guides;
